import React, { ReactNode } from 'react';

const RICH_HTML_TAG_RE = /<\/?(?:p|strong|em|ul|ol|li|br|b|i)\b/i;

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'B',
  'EM',
  'I',
  'UL',
  'OL',
  'LI'
]);

export function looksLikeRichNotificationHtml(text?: string | null): boolean {
  return !!text && RICH_HTML_TAG_RE.test(text);
}

function mapDomNodeToReact(node: ChildNode, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const el = node as Element;
  const tag = el.tagName.toUpperCase();
  const children = Array.from(el.childNodes).map((child, index) =>
    mapDomNodeToReact(child, `${key}-${index}`)
  );

  if (!ALLOWED_TAGS.has(tag)) {
    return <React.Fragment key={key}>{children}</React.Fragment>;
  }

  if (tag === 'BR') {
    return <br key={key} />;
  }

  const reactTag =
    tag === 'B' ? 'strong' : tag === 'I' ? 'em' : tag.toLowerCase();

  return React.createElement(reactTag, { key }, ...children);
}

/**
 * Parse TipTap/configurator HTML with browser DOMParser and map only allowlisted
 * tags to React nodes (no dangerouslySetInnerHTML, no external sanitizer).
 */
export function renderSafeNotificationHtml(html: string): ReactNode {
  if (!html) return null;

  if (typeof DOMParser === 'undefined') {
    return html.replace(/<[^>]+>/g, '');
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const bodyChildren = Array.from(doc.body.childNodes);

  if (bodyChildren.length === 0) {
    return null;
  }

  return bodyChildren.map((child, index) =>
    mapDomNodeToReact(child, `nhtml-${index}`)
  );
}
